import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { coreVendors } from '@nexxe/core-vendors';
import { iif, Observable, of, Subject, throwError } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import { CdsMetadata } from '../class/cds-metadata';
import { CDSMetadata } from '../class/CDSMetadata';
import { Status } from '../enums/status';
import { ICDSChange, ParamsType } from '../interfaces/icds';
import { ICDSConfig } from '../interfaces/icdsconfig';
import { ICDSOptions } from '../interfaces/icdsoptions';
import { IContext } from '../interfaces/irequestmetadata';
import { CdsStoreService, IRequest } from './cds-store.service';

@Injectable({
	providedIn: 'root'
})
export class CdsUtilitiesService {

	private sub = new Subject<{ key: string, id: string, response: any }>();
	public responseStream$ = this.sub.asObservable();
	constructor(private store: CdsStoreService) { }

	registerMetadata(config: ICDSConfig, storeId: string) {
		this.addConfigToStore(config, storeId);
		this.store.reset();
		this.store.activateStore(storeId || "default");
	}

	registerExistingMetaData(storeId: string) {
		if (this.store.currentConfigStoreId !== storeId) {
			this.store.reset();
			this.store.activateStore(storeId);
		}
	}

	addConfigToStore(config: ICDSConfig, storeId: string) {
		if (storeId === 'default' || !this.store.configStore.get(storeId))
			this.store.configStore.set(storeId, config);
		else
			throw new Error(`The storeId "${storeId}" already exists in CDS.`);

	}

	alreadyExistsInStore(storeId: string) {
		return this.store.configStore.get(storeId);
	}

	removeConfigFromStore(storeId: string) {
		if (this.store.configStore.has(storeId)) {
			const endpoints = coreVendors.lodash.reduce(Object.keys(this.store.configStore.get(storeId).source), (acc, endpoint) => { acc.push(endpoint); return acc; }, [])
			coreVendors.lodash.forEach(endpoints, endpoint => delete this.store.ActiveRequests[endpoint])
			this.store.configStore.delete(storeId);
		}
	}

	getMetadata(key: string): ICDSOptions {
		if (key) {
			if (this.store.CDSConfig.source[key]) return this.store.CDSConfig.source[key];
			else throw new Error('key not found');
		}
	}

	isExist(key): Boolean {
		return this.getMetadata(key) ? true : false;
	}

	verifyExistingRequest(potentialMatchingMetadata: CDSMetadata, override?: boolean): CDSMetadata;
	verifyExistingRequest(potentialMatchingMetadata: CdsMetadata, override?: boolean): CdsMetadata;

	verifyExistingRequest(potentialMatchingMetadata: any, override?: boolean): any {
		if (!override) {
			if (this.isExist(potentialMatchingMetadata.key)) {
				const cdsMetadata = this.hasInvokedBefore(potentialMatchingMetadata);
				if (cdsMetadata) {
					return !this.isExpired(cdsMetadata) ? cdsMetadata : null;
				}
			}
		}
		return null;
	}

	isExpired(cdsMetadata: CdsMetadata): boolean;
	isExpired(cdsMetadata: CDSMetadata): boolean;

	isExpired(cdsMetadata: any): boolean {
		if (cdsMetadata.status != Status.progress) {
			return cdsMetadata.isExpired;
		}
		return false;
	}

	hasInvokedBefore(potentialMatchingMetadata: CDSMetadata): CDSMetadata;

	hasInvokedBefore(potentialMatchingMetadata: CdsMetadata): CdsMetadata;

	hasInvokedBefore(potentialMatchingMetadata: any): any {
		//TODO add for loop to check if the object is available
		const request = this.store.ActiveRequests[potentialMatchingMetadata.key];
		if (request) {
			const calls = Object.keys(request);
			const iteratorThreshold = calls.length > this.store.MaximumCalls ? calls.length - this.store.MaximumCalls : 0;

			for (let i = calls.length - 1; i >= iteratorThreshold; i--) {
				if (potentialMatchingMetadata.endpoint === request[calls[i]].metadata.endpoint && coreVendors.lodash.isEqual(potentialMatchingMetadata.payload, request[calls[i]].metadata.payload) && coreVendors.lodash.isEqual(potentialMatchingMetadata.queryParams, request[calls[i]].metadata.queryParams)) {
					return request[calls[i]].metadata;
				}
			}
		}

		return null;

	}

	createNewRequest(key: string, params?: ParamsType, payload?: ParamsType, urlTemplate?: Object, isFormDataReq?: boolean, override?: boolean): string {
		/*using clone deep to save an instance of the the  Params to be used later in compare to validate if the API invoked before or not.*/
		const req: CdsMetadata = this.createCdsMetadata(key, params, payload, urlTemplate, isFormDataReq);
		let matchedMetadata: CdsMetadata;
		if (!this.store.ActiveRequests[req.key]) {
			this.store.ActiveRequests[req.key] = {};
		} else if (!isFormDataReq) {
			matchedMetadata = this.verifyExistingRequest(req, override);
		}
		if (!matchedMetadata || isFormDataReq) {
			const a = { [req.id]: { "metadata": req, "response": null } } as IRequest;
			this.store.ActiveRequests[req.key] = { ...this.store.ActiveRequests[req.key], ...a };
			return req.id;
		} else {
			return matchedMetadata.id
		}
	}

	createRequest(key: string, context?: IContext, isFormDataReq?: boolean, override?: boolean): string {
		/*using clone deep to save an instance of the the  Params to be used later in compare to validate if the API invoked before or not.*/
		const req: CDSMetadata = this.createCDSMetadata(key, context);
		let matchedMetadata: CDSMetadata;
		if (!this.store.ActiveRequests[req.key]) {
			this.store.ActiveRequests[req.key] = {};
		} else if (!isFormDataReq) {
			matchedMetadata = this.verifyExistingRequest(req, override);
		}
		if (!matchedMetadata || isFormDataReq) {
			const a = { [req.id]: { "metadata": req, "response": null } } as IRequest;
			this.store.ActiveRequests[req.key] = { ...this.store.ActiveRequests[req.key], ...a };
			return req.id;
		} else {
			return matchedMetadata.id
		}
	}

	createCdsMetadata(key: string, params?: ParamsType, payload?: ParamsType, urlTemplate?: Object, isFormDataReq?: boolean) {
		const id = this.getID();
		return new CdsMetadata(key, this.store.CDSConfig.source[key], this.store.CDSConfig, id, coreVendors.lodash.cloneDeep(params), isFormDataReq ? payload : coreVendors.lodash.cloneDeep(payload), urlTemplate);
	}

	createCDSMetadata(key: string, context?: IContext): CDSMetadata {
		const id = this.getID();
		return new CDSMetadata(key, this.store.CDSConfig.source[key], this.store.CDSConfig, id, context);
	}

	removeKey(key: string, id: string) {
		delete this.store.ActiveRequests[key][id];
	}



	publishResponse(key, id) {
		this.store.request.next({ patch: { key: key, id: id }, store: this.store.ActiveRequests[key][id].response } as ICDSChange);
	}

	getInProgressResponse(key, id) {
		return this.responseStream$.pipe(
			filter(_ => _.key === key && _.id === id),
			first()
		)

	}

	getExistingResponse(key: string, id: string) {
		return this.store.ActiveRequests[key][id].response;
	}

	getMetadataStatus(key: string, id: string) {
		const metadata = this.getCdsMetadata(key, id);
		return metadata.status;
	}

	getCdsMetadata(key: string, id: string) {
		return this.store.ActiveRequests[key] && this.store.ActiveRequests[key][id] && this.store.ActiveRequests[key][id].metadata;
	}

	/**
	 * 
	 * @param key 
	 * @description return an observable for the triggered API
	 */
	getResponse(keys: string | Array<string>, ids: string | Array<string>): Observable<ICDSChange> {
		return this.store.stream$.pipe(
			filter(_ => _.patch.key == keys && _.patch.id == ids),
			first(),
			mergeMap(change => iif(() =>
				change.store instanceof HttpErrorResponse,
				<Observable<ICDSChange>>throwError(change),
				<Observable<ICDSChange>>of(change)
			)
			)
		);
	}

	getFormDataResponse(keys: string | Array<string>, ids: string | Array<string>) {
		return this.store.stream$.pipe(filter(_ => _.patch.key == keys && _.patch.id == ids), map((_) => {
			setTimeout(() => {
				this.removeKey(<string>keys, <string>ids);
			});
			return _
		}))
	}

	// #region CDSOptions

	updateStatus(key, status): void {
		const node = this.getMetadata(key);
		node.status = status;
	}

	updateStats(key, id, stats: number): void {
		const node = this.getCdsMetadata(key, id);
		node && (node.lastSuccess = stats);
	}

	updateResponse(key: string, id: string, response): void {
		this.store.ActiveRequests[key] && this.store.ActiveRequests[key][id] && ((this.store.ActiveRequests[key][id].response = response) && this.sub.next({ key, id, response }));
	}

	// #endregion


	// #region Queue

	updateQueue(req: CdsMetadata,): void {
		if (!this.store.ActiveRequests[req.key]) {
			this.store.ActiveRequests[req.key] = {};
		}
		const a = { [req.id]: { "params": req.queryParams, "payload": req.payload } };
	}
	removeFromQueue(req: CdsMetadata): void {
		this.store.ActiveRequests[req.key] && coreVendors.lodash.omit(this.store.ActiveRequests, [req.key]);
	}
	isInQueue(req: CdsMetadata): boolean {
		return coreVendors.lodash.has(this.store.ActiveRequests, req.key);
	}
	checkAvailableInQueue(key: string): boolean {
		return this.store.ActiveRequests[key] ? true : false;
	}


	// #endregion



	getID(): string {
		// Math.random should be unique because of its seeding algorithm.
		// Convert it to base 36 (numbers + letters), and grab the first 9 characters
		// after the decimal.
		return '_' + Math.random().toString(36).substr(2, 9);
	}

}
